import { useState } from 'react'
import MarketsGrid from '../components/MarketsGrid'
import { useMarkets } from '../hooks/useMarkets'
import { formatCurrency, formatPercentage, getPriceChangeColor } from '../utils/formatters'

const Markets = () => {
  const { markets } = useMarkets()
  const [sortBy, setSortBy] = useState('symbol')
  const [filterCategory, setFilterCategory] = useState('all')

  const categories = [
    { id: 'all', name: 'All Markets' },
    { id: 'crypto', name: 'Cryptocurrency' },
    { id: 'stocks', name: 'Stocks' },
    { id: 'forex', name: 'Forex' },
    { id: 'commodities', name: 'Commodities' }
  ]

  const getCategory = (symbol) => {
    if (symbol.includes('BTC') || symbol.includes('ETH')) return 'crypto'
    if (symbol.includes('USD') && !symbol.includes('BTC') && !symbol.includes('ETH')) return 'forex'
    if (symbol === 'AAPL' || symbol === 'SPX') return 'stocks'
    if (symbol.includes('XAU')) return 'commodities'
    return 'all'
  }

  const filteredMarkets = markets.filter(market => 
    filterCategory === 'all' || getCategory(market.symbol) === filterCategory
  )

  const sortedMarkets = [...filteredMarkets].sort((a, b) => {
    switch (sortBy) {
      case 'symbol':
        return a.symbol.localeCompare(b.symbol)
      case 'price':
        return b.price - a.price
      case 'change':
        return b.change24h - a.change24h
      default:
        return 0
    }
  })

  return (
    <div className="min-h-screen bg-neutral dark:bg-secondary">
      {/* Header */}
      <section className="bg-gradient-primary text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Global Markets
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl">
            Track real-time prices across crypto, forex, stocks, and commodities. Access deep liquidity and execute trades with institutional-grade infrastructure.
          </p>
        </div>
      </section>

      {/* Filters and Controls */}
      <section className="bg-white dark:bg-secondary-light border-b border-gray-200 dark:border-gray-700 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            {/* Category Filters */}
            <div className="flex flex-wrap gap-2">
              {categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => setFilterCategory(category.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all focus:outline-none focus:ring-2 focus:ring-primary ${
                    filterCategory === category.id
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>

            {/* Sort Dropdown */}
            <div className="flex items-center space-x-2">
              <label htmlFor="sort" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Sort by:
              </label>
              <select
                id="sort"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-secondary text-secondary dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="symbol">Symbol</option>
                <option value="price">Price</option>
                <option value="change">24h Change</option>
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Markets Grid */}
      <section className="section-container">
        <MarketsGrid limit={null} />
      </section>

      {/* Market Table */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="card overflow-x-auto">
          <h2 className="text-2xl font-bold text-secondary dark:text-white mb-6">
            Detailed Market Data
          </h2>
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Symbol</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Name</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Price</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">24h Change</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Volume</th>
              </tr>
            </thead>
            <tbody>
              {sortedMarkets.map((market) => (
                <tr
                  key={market.id}
                  className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <td className="py-4 px-4 font-medium text-secondary dark:text-white">
                    {market.symbol}
                  </td>
                  <td className="py-4 px-4 text-gray-600 dark:text-gray-400">
                    {market.name}
                  </td>
                  <td className="py-4 px-4 text-right font-medium text-secondary dark:text-white">
                    {formatCurrency(market.price)}
                  </td>
                  <td className={`py-4 px-4 text-right font-medium ${getPriceChangeColor(market.change24h)}`}>
                    {formatPercentage(market.change24h)}
                  </td>
                  <td className="py-4 px-4 text-right text-gray-600 dark:text-gray-400">
                    {market.volume}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}

export default Markets
